import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Captures Gap Finder quiz activity from the public landing page (anon client,
 * mirroring useWaitlistSubmit). Insert-only: answers are recorded on completion
 * whether or not an email is given; if the visitor provides an email, a second
 * row is inserted with the same session_id, and a DB trigger collapses the two
 * into one row per completion (email row wins). Update/upsert is avoided —
 * PostgREST can't target an anon row for update without a SELECT policy, and we
 * keep leads staff-read-only.
 *
 * No reward email is sent from here: the 10% code is shown on the result screen,
 * and the quiz component adds the visitor to the waitlist (which fires the
 * existing "waitlist joined" email).
 */
export function useQuizSubmit() {
  // Record the answers as soon as the quiz is finished — no email required.
  const recordAnswers = useCallback(async (sessionId: string, picks: number[]): Promise<void> => {
    try {
      // `quiz_leads` is a new table not yet in the generated Supabase types.
      await (supabase as any).from("quiz_leads").insert({ session_id: sessionId, picks });
    } catch (err) {
      console.error("quiz_leads recordAnswers failed", err);
    }
  }, []);

  // Capture the email as its own row (the trigger merges it with the answers row).
  const submitEmail = useCallback(async (sessionId: string, email: string, picks: number[]): Promise<void> => {
    const normEmail = email.trim().toLowerCase();
    try {
      await (supabase as any)
        .from("quiz_leads")
        .insert({ session_id: sessionId, email: normEmail, picks });
    } catch (err) {
      console.error("quiz_leads submitEmail failed", err);
    }
  }, []);

  return { recordAnswers, submitEmail };
}
